import { useInfiniteQuery, useQuery, UseQueryResult } from 'react-query';
import { SchemaInfiniteQueryOptions } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';
import { TaskCenterTypes } from '../';
import { DEFAULT_TASK_LIMIT, TaskCenterService, TASK_BY_ID_QUERY_KEY } from '../constants';
import { SortField, Filter, ListTasksResponse, TaskSource } from '../types';

const getTasksApi = TaskCenterService.ListTasks;

export const useGetTasksWithFilters = (
  queryKeys: string[],
  filter: Record<number, Filter>,
  sortType?: SortField,
  options: SchemaInfiniteQueryOptions<SchemaIO<typeof TaskCenterService.ListTasks>> = {}
) => {
  return useInfiniteQuery<ListTasksResponse, unknown, ListTasksResponse>({
    ...options,
    queryKey: queryKeys,
    queryFn: async ({ pageParam }) => {
      return await getTasksApi({
        filter,
        limit: DEFAULT_TASK_LIMIT,
        sortField: sortType,
        includeMetadata: true,
        cursor: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.cursor === 'undefined' || (lastPage.tasks && lastPage.tasks?.length < DEFAULT_TASK_LIMIT))
        return undefined;
      return lastPage.cursor;
    },
    keepPreviousData: true,
  });
};

export const useGetTaskDetails = (
  locationIds: string[],
  taskId?: string | null
): UseQueryResult<ListTasksResponse, unknown> => {
  const filter: Record<number, Filter> = {
    [TaskCenterTypes.FilterType_index.FILTER_BY_TASK_ID]: {
      taskId: taskId ? [taskId] : undefined,
      locationId: locationIds,
    },
    [TaskCenterTypes.FilterType_index.FILTER_BY_LOCATION]: { locationId: locationIds },
  };

  return useQuery<ListTasksResponse, unknown, ListTasksResponse>({
    queryKey: [TASK_BY_ID_QUERY_KEY, taskId],
    queryFn: async () => {
      return await getTasksApi({
        filter,
        limit: DEFAULT_TASK_LIMIT,
        includeMetadata: true,
      });
    },
    enabled: !!taskId,
    queryKeyHashFn: () => JSON.stringify({ filter }),
  });
};

export const useGetTaskBySourceId = ({
  sourceId,
  source,
  locationIds,
}: {
  sourceId: string | undefined;
  source: TaskSource;
  locationIds: string[];
}) => {
  const filter: Record<number, Filter> = {
    [TaskCenterTypes.FilterType_index.FILTER_BY_SOURCE]: {
      source,
      locationId: locationIds,
    },
    [TaskCenterTypes.FilterType_index.FILTER_BY_SOURCE_ID]: {
      locationId: locationIds,
      sourceId: sourceId ? [sourceId] : [],
    },
    [TaskCenterTypes.FilterType_index.FILTER_BY_LOCATION]: { locationId: locationIds },
  };

  return useQuery({
    queryKey: [TASK_BY_ID_QUERY_KEY],
    queryFn: async () =>
      await getTasksApi({
        filter,
        limit: DEFAULT_TASK_LIMIT,
        includeMetadata: true,
      }),
    enabled: !!source && !!sourceId,
  });
};
